import React from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { getTextColorByBackground } from 'utils/ColorUtil'
import { Box, Chip, Grid, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import CheckBoxIcon from '@mui/icons-material/CheckBox'

const STATUS = {
    0: 'En cours de saisie',
    1: 'Vérifié',
}

const COLORS_BY_STATUS = {
    0: {
        color: '#2f4f78',
        backgroundColor: '#d8e7ff',
    },
    1: {
        color: '#8b5e2e',
        backgroundColor: '#ffe8a3',
    },
}

const COLOR_BY_ADVENCEMENT = {
    empty: 'transparent',
    default: '#3e78bc',
    completed: '#4ca96a',
}

const CIRCLE_TRACK_COLOR = '#e7edf5'

const IndicatorCategoryCard = ({
    card,
    height,
    variant = 'primary',
}) => {
    const primary = variant === 'primary'

    const cardColor = COLORS_BY_STATUS[card.status]
    const strokeColor = card.advencement === 0
        ? COLOR_BY_ADVENCEMENT.empty
        : card.advencement === 100
            ? COLOR_BY_ADVENCEMENT.completed
            : COLOR_BY_ADVENCEMENT.default

    const boxSize = primary ? '4rem' : '3rem'
    const viewBox = primary ? '0 0 36 36' : '0 0 24 24'
    const circleSize = primary ? 18 : 12
    const circleRadius = primary ? 15 : 10
    const circleStrokeWidth = primary ? 3 : 2

    return (
        <Grid
            container
            columnSpacing={1}
            key={card.title}
            sx={{
                height,
                backgroundColor: card.color,
                borderRadius: '1rem',
                border: '1px solid black',
                padding: '0.5rem 1rem',
                color: getTextColorByBackground(card.color),
            }}
        >
            <Grid
                container
                size='grow'
                direction='column'
                rowSpacing={0.5}
            >
                <Typography
                    variant='h6'
                    sx={{
                        paddingBottom: '1rem',
                        textAlign: primary ? 'initial' : 'center',
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word',
                    }}
                >
                    {card.title}
                </Typography>
                <Grid
                    container
                    sx={{ flex: 1, alignItems: primary ? 'flex-start' : 'center' }}
                >
                    <Chip
                        size='small'
                        label={STATUS[card.status]}
                        icon={card.status === 0 ? <EditIcon /> : <CheckBoxIcon />}
                        sx={{
                            backgroundColor: cardColor.backgroundColor,
                            color: cardColor.color,
                            border: `1px solid ${cardColor.color}`,
                        }}
                    />
                </Grid>
                {primary && card.nbIndicatorsTotal && (
                    <Grid>{card.nbIndicators} {i18n.indicators} {i18n.of} {card.nbIndicatorsTotal}</Grid>
                )}
            </Grid>
            <Box
                sx={{
                    position: 'relative',
                    width: boxSize,
                    height: boxSize,
                    color: 'black',
                }}
            >
                <svg
                    width='100%'
                    height='100%'
                    viewBox={viewBox}
                    style={{
                        transform: 'rotate(-90deg)',
                        backgroundColor: 'transparent',
                    }}
                >
                    <circle
                        cx={circleSize}
                        cy={circleSize}
                        r={circleRadius}
                        fill='#fff'
                        stroke={CIRCLE_TRACK_COLOR}
                        strokeWidth={circleStrokeWidth}
                    />
                    <circle
                        cx={circleSize}
                        cy={circleSize}
                        r={circleRadius}
                        fill='none'
                        stroke={strokeColor}
                        strokeWidth={circleStrokeWidth}
                        strokeLinecap='round'
                        strokeDasharray={`${card.advencement}, 100`}
                    />
                </svg>

                <Typography
                    variant='body2'
                    fontWeight='bold'
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {card.advencement}%
                </Typography>
            </Box>
        </Grid>
    )
}

IndicatorCategoryCard.propTypes = {
    height: PropTypes.string,
    variant: PropTypes.oneOf(['primary', 'secondary']),
    card: PropTypes.shape({
        title: PropTypes.string,
        advencement: PropTypes.number,
        status: PropTypes.number,
        nbIndicators: PropTypes.number,
        nbIndicatorsTotal: PropTypes.number,
        color: PropTypes.string,
    }),
}

export default IndicatorCategoryCard
